// WizardStep.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './WizardStep.css';

// Components
import { motion, AnimatePresence } from 'framer-motion';

const addons = {
  enter: (direction) => {
    if (direction === 0) {
      return {
        x: 0,
        opacity: 1,
        position: 'relative',
      };
    }
    return {
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      position: 'absolute',
    };
  },
  center: {
    x: 0,
    opacity: 1,
    position: 'relative',
  },
  exit: (direction) => {
    if (direction === 0) {
      return {
        x: 0,
        opacity: 0,
        position: 'absolute',
      };
    }
    return {
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      position: 'absolute',
    };
  },
};

/**
 * WizardStep Component
 * 
 * Renders a single step in a multi-step wizard interface with animated transitions.
 * 
 * @param {object} props - Component props
 * @param {number} props.step - The step number of this WizardStep
 * @param {number} props.currentStep - The current active step number
 * @param {number} props.direction - The direction of navigation (1 for next, -1 for previous, 0 for initial)
 * @param {boolean} props.isAutoHeight - Whether the step should auto adjust its height
 * @param {React.ReactNode} props.children - The content of the WizardStep
 * 
 * @returns {ReactNode} The rendered WizardStep component.
 */
const WizardStep = ({ step, currentStep, direction, isAutoHeight, children }) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // Determines if this step is the active step
  const isActive = currentStep === step;

  const style = isAutoHeight
    ? { height: 'auto', flex: 'none' }
    : (isMobile ? { height: '100%' } : { flex: 1 });

  // ----- RENDER ----- //

  return (
    <AnimatePresence custom={direction} mode="wait">
      {isActive && (
        <motion.div
          className="wizard-step-container"
          style={style}
          key={`step${step}`}
          custom={direction}
          addons={addons}
          initial="enter"
          animate="center"
          exit="exit"
          transition={direction === 0 ? { duration: 0 } : { duration: 0.2, ease: 'easeInOut' }}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, { isAutoHeight });
            }
            return child;
          })}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Dev Tools
WizardStep.displayName = 'WizardStep';

export default WizardStep;
